import React, { useEffect, useMemo, useState } from 'react';
import {
  DefaultFactorColumn,
  Footer,
  TitleBar,
} from '../../../../../components';
import QueryErrorBoundary from '../../../../../components/error-handling';
import TableV3 from '../../../../../components/generic/Table/TableV3';
import { Checkbox, message, TableColumnsType, Tooltip } from 'antd';
import { tableSortingMethods } from '../../../../../utils';
import { checkForWorkaroundsAffectedByITAppRemoval } from '../../../../../utils/checkForWorkaroundsAffectedByITAppRemoval';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { FETCH_IT_APPLICATIONS } from '../../../../dataInputs/queries/itApplications';
import {
  QualitativeFactor,
  QualitativeFactorsDashboard,
  SelectedITApps,
} from './types';
import { ITApplication } from '../application-revenue-mapping';
import {
  GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS,
  GET_QUALITATIVE_FACTORS_DASHBOARD,
  SET_IT_APPLICATIONS_TO_QUALITATIVE_FACTORS,
} from './queries';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import { useRemoveITApplicationConfirmation } from '../../../location-applications/business-functions-workarounds/hooks';
import { useQualitativeFactorWorkarounds } from '../../../pages/workarounds/qualitative-factors/hooks';
import { RemoveITApplicationModal } from '../../../components/RemoveITApplicationModal';

type ITapps = {
  findAllItApplications: ITApplication[];
};

type BulkMatrixType = QualitativeFactor & ITApplication;

const QualitativeFactorsBulk = () => {
  const { data, loading, error } = useQuery<QualitativeFactorsDashboard>(
    GET_QUALITATIVE_FACTORS_DASHBOARD,
  );

  const qualitativeFactorsData = data?.getQualitativeFactorsDashboard || [];
  const orderedQualitativeFactorsData = qualitativeFactorsData?.toSorted(
    (a, b) => Number(b?.isDefault) - Number(a?.isDefault),
  );
  const filteredQualitativeFactors = orderedQualitativeFactorsData?.filter(
    (bf) => !bf?.supportedByITApplication,
  );
  const qfsIds = filteredQualitativeFactors.map((qf) => qf.uuid);
  const { data: workarounds } = useQualitativeFactorWorkarounds(qfsIds);

  const {
    data: qfselectedITApps,
    loading: qfSelectedITAppsLoading,
    error: qfError,
  } = useQuery<SelectedITApps>(GET_QUALITATIVE_FACTOR_SELECTED_IT_APPS, {
    variables: {
      data: qfsIds,
    },
    skip: qfsIds.length === 0,
  });

  const qfselectedITAppsData = useMemo(
    () => qfselectedITApps?.getQualitativeFactorsSelectedITApplications ?? [],
    [qfselectedITApps],
  );

  const {
    data: ITapps,
    loading: ITappsLoading,
    error: ITappsError,
  } = useQuery<ITapps>(FETCH_IT_APPLICATIONS, {
    variables: {
      data: {
        enterpriseWide: true,
      },
    },
  });

  const [bulkITapps, { loading: bulkUpdateLoading }] = useMutation(
    SET_IT_APPLICATIONS_TO_QUALITATIVE_FACTORS,
  );

  const ITappsData = ITapps?.findAllItApplications || [];

  const loadingStates =
    loading || ITappsLoading || bulkUpdateLoading || qfSelectedITAppsLoading;

  const [checkedState, setCheckedState] = useState<{
    [key: string]: Set<string>;
  }>({});

  const {
    isModalOpen,
    handleBeforeUpdate,
    affectedApplications,
    handleConfirm,
    handleCancel,
  } = useRemoveITApplicationConfirmation({
    workarounds: workarounds ?? [],
  });

  /**
   * Handles adding an IT application to a qualitative factor
   * Updates the local state and saves changes to the server
   */
  const handleAddITApplication = (
    qualitativeFactorId: string,
    itAppUuid: string,
    currentChecked: Set<string>,
  ) => {
    // Create a new set with the added IT application
    const updatedCheckedSet = new Set(currentChecked);
    updatedCheckedSet.add(itAppUuid);

    // Update local state immediately for responsive UI
    setCheckedState((prev) => ({
      ...prev,
      [qualitativeFactorId]: updatedCheckedSet,
    }));

    // Save changes to server
    handleBulkUpdate(qualitativeFactorId, updatedCheckedSet);
  };

  /**
   * Handles removing an IT application from a qualitative factor
   * Checks for workarounds before removal and shows confirmation if needed
   */
  const handleRemoveITApplication = (
    qualitativeFactorId: string,
    itAppUuid: string,
    currentChecked: Set<string>,
  ) => {
    // Create a new set without the removed IT application
    const updatedCheckedSet = new Set(currentChecked);
    updatedCheckedSet.delete(itAppUuid);

    // Filter workarounds to only include those for this specific qualitative factor
    const relevantWorkarounds = (workarounds ?? []).filter(
      (workaround: any) =>
        workaround.qualitativeFactorId === qualitativeFactorId,
    );

    // Pass the IT application being removed directly
    const appsBeingRemoved = [{ uuid: itAppUuid }];

    // Check if removing this IT app would break any existing backup/recovery strategies
    // Only for THIS specific qualitative factor
    const { hasUpdatedWorkarounds } = checkForWorkaroundsAffectedByITAppRemoval(
      relevantWorkarounds,
      appsBeingRemoved,
    );

    if (hasUpdatedWorkarounds) {
      // Show confirmation modal with the specific workarounds for this QF
      handleBeforeUpdate(appsBeingRemoved, () => {
        // Update local state after confirmation
        setCheckedState((prev) => ({
          ...prev,
          [qualitativeFactorId]: updatedCheckedSet,
        }));

        // Save changes to server
        handleBulkUpdate(qualitativeFactorId, updatedCheckedSet);
      });
    } else {
      // No workarounds for this specific QF + IT app combination, proceed directly
      setCheckedState((prev) => ({
        ...prev,
        [qualitativeFactorId]: updatedCheckedSet,
      }));

      // Save changes to server
      handleBulkUpdate(qualitativeFactorId, updatedCheckedSet);
    }
  };

  /**
   * Saves the updated IT application assignments for a qualitative factor to the server
   * @param qualitativeFactorId - The UUID of the qualitative factor being updated
   * @param updatedChecked - Set of IT application UUIDs that should be assigned to this factor
   */
  const handleBulkUpdate = async (
    qualitativeFactorId: string,
    updatedChecked: Set<string>,
  ) => {
    try {
      const ITappsIds = Array.from(updatedChecked).map((uuid) => ({ uuid }));

      const response = await bulkITapps({
        variables: {
          data: {
            qualitativeFactors: [
              { itApplications: ITappsIds, uuid: qualitativeFactorId },
            ],
          },
        },
      });

      if (response?.data.setItApplicationsToQualitativeFactor) {
        message.success('Changes Saved');
      }
    } catch (e) {
      message.error(
        e instanceof ApolloError ? e.message : 'Something went wrong!',
      );
      console.log('error: ', e);
    }
  };

  const ITappsColumns: TableColumnsType<BulkMatrixType> = ITappsData.map(
    (ITapp) => ({
      key: ITapp.uuid,
      title: ITapp.application,
      align: 'center',
      render: (_, record) => {
        const qualitativeFactorId = record.uuid;
        const currentChecked =
          checkedState[qualitativeFactorId] || new Set<string>();
        const isChecked = currentChecked.has(ITapp.uuid);

        return (
          <Checkbox
            className="[&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-[#437EF7] [&_.ant-checkbox:hover_.ant-checkbox-inner]:!border-[#437EF7] [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-[#437EF7]"
            checked={isChecked}
            onChange={(e) => {
              // Handle adding an IT application to a qualitative factor
              if (e.target.checked) {
                handleAddITApplication(
                  qualitativeFactorId,
                  ITapp.uuid,
                  currentChecked,
                );
              } else {
                // Handle removing an IT application from a qualitative factor
                handleRemoveITApplication(
                  qualitativeFactorId,
                  ITapp.uuid,
                  currentChecked,
                );
              }
            }}
          />
        );
      },
    }),
  );

  const columns: TableColumnsType<BulkMatrixType> = [
    {
      title: 'Qualitative Factor',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      fixed: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string, record: QualitativeFactor) => (
        <DefaultFactorColumn text={text} isDefault={record?.isDefault} />
      ),
    },
    ...ITappsColumns,
  ];

  useEffect(() => {
    if (qfselectedITAppsData.length > 0) {
      const initialState: { [key: string]: Set<string> } = {};
      qfselectedITAppsData.forEach((bf) => {
        initialState[bf.uuid] = new Set(bf.itApplications.map((it) => it.uuid));
      });
      setCheckedState(initialState);
    }
  }, [qfselectedITAppsData]);

  return (
    <>
      <TitleUpdater title={`Enterprise Qualitative Factor Tagging`} />
      <TitleBar
        title="Enterprise Qualitative Factor Tagging"
        subtitle={`Bulk Application Tagging`}
        brief="In the matrix below, check the relevant boxes to show how applications support the various factors in the organization. Note, that this has been pre-selected based on when the application was first added to MEO."
      />
      <QueryErrorBoundary error={[error, ITappsError, qfError]}>
        <TableV3
          columns={columns}
          data={filteredQualitativeFactors}
          loading={loadingStates}
          useBorderedSection
          editable
          editableOptions={{
            update: false,
          }}
        />
      </QueryErrorBoundary>
      <RemoveITApplicationModal
        isOpen={isModalOpen}
        onCancel={handleCancel}
        onRemove={handleConfirm}
        applications={affectedApplications}
      />
      <Footer backPath={'/cyber/enterprise-level/qualitative-factors'} />
    </>
  );
};

export default QualitativeFactorsBulk;
